import { useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2 } from "lucide-react";
import CbamSyncReviewDialog from "./CbamSyncReviewDialog";

// Locale-aware numeric parse for CSV cells. A bare parseFloat turns UK/EU "1,234.5" into 1 (it stops
// at the thousands comma) and "12,5" into 12. This handles both grouping conventions:
//  • both separators present → the right-most is the decimal, the other is grouping
//  • only commas → grouped integer ("1,234"/"1,234,567") is thousands; otherwise a decimal comma
function parseLocaleNumber(raw: string | null | undefined): number {
  if (raw == null) return NaN;
  let s = String(raw).trim().replace(/[%\s ]/g, "");
  if (s === "") return NaN;
  const neg = /^\(.*\)$/.test(s); // accounting-style negatives
  s = s.replace(/[()]/g, "");
  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  if (hasComma && hasDot) {
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) s = s.replace(/\./g, "").replace(",", "."); // 1.234,5 -> 1234.5
    else s = s.replace(/,/g, ""); // 1,234.5 -> 1234.5
  } else if (hasComma) {
    if (/^\d{1,3}(,\d{3})+$/.test(s)) {
      // Looks like grouped thousands (e.g. 1,234). But asset weights are bounded 0–100, so a single
      // comma whose thousands reading would exceed 100 is really an EU decimal comma ("1,000" = 1.0);
      // prefer the decimal in that case. Multi-group values (1,234,567) stay thousands.
      const asThousands = parseFloat(s.replace(/,/g, ""));
      const singleComma = (s.match(/,/g) || []).length === 1;
      s = singleComma && asThousands > 100 ? s.replace(",", ".") : s.replace(/,/g, "");
    } else {
      s = s.replace(",", "."); // decimal comma
    }
  }
  const n = parseFloat(s);
  if (isNaN(n)) return NaN;
  return neg ? -n : n;
}

// Parse a CSV line respecting quoted fields (including embedded newlines).
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") {
        cur.push(field);
        field = "";
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i++;
        cur.push(field);
        field = "";
        if (cur.length > 1 || cur[0] !== "") rows.push(cur);
        cur = [];
      } else field += ch;
    }
  }
  if (field.length || cur.length) {
    cur.push(field);
    rows.push(cur);
  }
  return rows;
}

// Normalize FE column header: collapse newlines/whitespace.
function normalizeHeader(h: string): string {
  return h.replace(/\s+/g, " ").replace(/^\uFEFF/, "").trim();
}

interface ParsedFund {
  isin: string;
  fund_name: string;
  breakdown: Record<string, number>;
  yield_pct: number;
}

export default function FEAssetBreakdownUploader({ onUploaded }: { onUploaded?: () => void }) {
  const [parsed, setParsed] = useState<ParsedFund[] | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = (ev.target?.result as string) ?? "";
        const rows = parseCsv(text);
        // FE export has a title preamble; find the row that contains "ISIN Code".
        const headerIdx = rows.findIndex((r) => r.some((c) => normalizeHeader(c) === "ISIN Code"));
        if (headerIdx < 0) {
          toast.error("Could not find header row containing 'ISIN Code'.");
          return;
        }
        const headers = rows[headerIdx].map(normalizeHeader);
        const isinIdx = headers.indexOf("ISIN Code");
        const nameIdx = headers.indexOf("Name");
        const yieldIdx = headers.indexOf("Yield");
        const histYieldIdx = headers.indexOf("Historic Yield");
        const assetCols: { idx: number; name: string }[] = headers
          .map((h, i) => ({ idx: i, name: h }))
          .filter((c) => c.name.startsWith("Asset %"));

        const funds: ParsedFund[] = [];
        const seen = new Set<string>();
        for (let i = headerIdx + 1; i < rows.length; i++) {
          const row = rows[i];
          const isin = (row[isinIdx] ?? "").trim().toUpperCase();
          const name = (row[nameIdx] ?? "").trim();
          if (!isin || !name) continue;
          if (seen.has(isin)) continue;
          seen.add(isin);
          const breakdown: Record<string, number> = {};
          for (const c of assetCols) {
            const v = parseLocaleNumber(row[c.idx]);
            if (!isNaN(v) && v > 0) breakdown[c.name] = v;
          }
          const yRaw = yieldIdx >= 0 ? (row[yieldIdx] ?? "").trim() : "";
          const hyRaw = histYieldIdx >= 0 ? (row[histYieldIdx] ?? "").trim() : "";
          let yieldPct = 0;
          const yNum = parseLocaleNumber(yRaw);
          if (yRaw !== "" && !isNaN(yNum)) {
            yieldPct = yNum;
          } else {
            const hyNum = parseLocaleNumber(hyRaw);
            if (hyRaw !== "" && !isNaN(hyNum)) yieldPct = hyNum;
          }
          funds.push({ isin, fund_name: name, breakdown, yield_pct: yieldPct });
        }
        setParsed(funds);
        toast.success(`Parsed ${funds.length} funds from CSV`);
      } catch (err: any) {
        toast.error("Parse error: " + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!parsed?.length) return;
    setIsUploading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const today = new Date().toISOString().split("T")[0];
      const rows = parsed.map((f) => ({
        isin: f.isin,
        fund_name: f.fund_name,
        as_of_date: today,
        breakdown: f.breakdown,
        yield_pct: f.yield_pct,
        uploaded_by: userData.user?.id ?? null,
      }));
      for (let i = 0; i < rows.length; i += 200) {
        const batch = rows.slice(i, i + 200);
        const { error } = await supabase
          .from("fe_asset_breakdown")
          .upsert(batch, { onConflict: "isin,as_of_date" });
        if (error) throw error;
      }
      toast.success(`Uploaded ${rows.length} FE rows`);
      setParsed(null);
      if (fileRef.current) fileRef.current.value = "";
      onUploaded?.();
      setReviewOpen(true);
    } catch (err: any) {
      toast.error("Upload failed: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Upload className="h-4 w-4" />
          FE Asset Breakdown CSV Upload
        </CardTitle>
        <CardDescription>
          Upload the FE export ("Total Return Custom table 'Asset Breakdown'"). Each row's
          Asset % columns are stored as the fund's breakdown and converted to CBAM Master Sectors
          via the lookup table.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input ref={fileRef} type="file" accept=".csv" onChange={handleFile} />
        {parsed && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              {parsed.length} funds parsed
            </div>
            <div className="max-h-64 overflow-auto border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">ISIN</th>
                    <th className="text-left p-2">Fund</th>
                    <th className="text-right p-2">Non-zero cols</th>
                    <th className="text-right p-2">Sum %</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.slice(0, 50).map((f) => {
                    const sum = Object.values(f.breakdown).reduce((a, b) => a + b, 0);
                    return (
                      <tr key={f.isin} className="border-b">
                        <td className="p-2 font-mono">{f.isin}</td>
                        <td className="p-2">{f.fund_name}</td>
                        <td className="p-2 text-right">{Object.keys(f.breakdown).length}</td>
                        <td className="p-2 text-right font-mono">{sum.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {parsed.length > 50 && (
                <div className="p-2 text-xs text-muted-foreground text-center">
                  + {parsed.length - 50} more rows
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpload} disabled={isUploading}>
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Uploading…
                  </>
                ) : (
                  "Confirm & Upload"
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setParsed(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
      <CbamSyncReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} />
    </Card>
  );
}
